import { useId, useMemo, useState } from "react";
import { getDistricts, getProvinces, ubigeoDepartments, type UbigeoValue } from "./ubigeo/ubigeo";

export interface UbigeoFieldNames {
  department: string;
  province: string;
  district: string;
}

export interface UbigeoSelectLabels {
  department: string;
  province: string;
  district: string;
}

export interface UbigeoSelectProps {
  /** Modo controlado. Si se pasa, el componente refleja este valor. */
  value?: UbigeoValue | undefined;
  /** Modo no controlado (p. ej. envíos por FormData). */
  defaultValue?: Partial<UbigeoValue> | undefined;
  onChange?: ((value: UbigeoValue) => void) | undefined;
  /** Nombres de los campos para envíos nativos por formulario. */
  names?: Partial<UbigeoFieldNames> | undefined;
  labels?: Partial<UbigeoSelectLabels> | undefined;
  errors?: Partial<Record<keyof UbigeoValue, string | null | undefined>> | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  idPrefix?: string | undefined;
  className?: string | undefined;
  selectClassName?: string | undefined;
}

const DEFAULT_NAMES: UbigeoFieldNames = {
  department: "department",
  province: "province",
  district: "district",
};

const DEFAULT_LABELS: UbigeoSelectLabels = {
  department: "Departamento",
  province: "Provincia",
  district: "Distrito",
};

const selectClass =
  "bg-paper font-body text-ink focus-visible:ring-gold focus-visible:ring-offset-paper h-11 w-full rounded-md border px-3 text-base transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

function normalize(value?: Partial<UbigeoValue> | null): UbigeoValue {
  return {
    department: value?.department ?? "",
    province: value?.province ?? "",
    district: value?.district ?? "",
  };
}

/**
 * Conserva el valor actual aunque no exista en el dataset (p. ej. datos
 * heredados) para no perder ni sustituir la ubicación guardada.
 */
function withCurrentValue(options: string[], current: string): string[] {
  if (current && !options.includes(current)) return [current, ...options];
  return options;
}

interface FieldConfig {
  id: string;
  label: string;
  name: string;
  selectValue: string;
  options: string[];
  placeholder: string;
  selectDisabled: boolean;
  required: boolean;
  error: string | null | undefined;
  onChange: (next: string) => void;
}

/**
 * Selectores dependientes de Departamento, Provincia y Distrito usando los
 * ubigeos locales de `@vimos/ui`. Funciona en modo controlado (`value`) y no
 * controlado (`defaultValue` + `names`), y conserva valores legados que no
 * estén en el dataset.
 */
export function UbigeoSelect({
  value,
  defaultValue,
  onChange,
  names,
  labels,
  errors,
  required = false,
  disabled = false,
  idPrefix,
  className = "",
  selectClassName = "",
}: UbigeoSelectProps) {
  const reactId = useId();
  const prefix = idPrefix ?? reactId;
  const fieldNames: UbigeoFieldNames = { ...DEFAULT_NAMES, ...names };
  const fieldLabels: UbigeoSelectLabels = { ...DEFAULT_LABELS, ...labels };
  const isControlled = value !== undefined;

  const [internal, setInternal] = useState<UbigeoValue>(() => normalize(defaultValue));
  const current = isControlled ? normalize(value) : internal;

  const departmentOptions = useMemo(
    () =>
      withCurrentValue(
        ubigeoDepartments.map((item) => item.name),
        current.department,
      ),
    [current.department],
  );
  const provinceOptions = useMemo(
    () =>
      withCurrentValue(
        getProvinces(current.department).map((item) => item.name),
        current.province,
      ),
    [current.department, current.province],
  );
  const districtOptions = useMemo(
    () =>
      withCurrentValue(
        getDistricts(current.department, current.province).map((item) => item.name),
        current.district,
      ),
    [current.department, current.province, current.district],
  );

  function emit(next: UbigeoValue) {
    if (!isControlled) setInternal(next);
    onChange?.(next);
  }

  function field(config: FieldConfig) {
    const { id, label, name, selectValue, options, placeholder, selectDisabled, error } = config;
    const errorId = error ? `${id}-error` : undefined;
    return (
      <div className="flex flex-col gap-1.5">
        <label htmlFor={id} className="font-body text-ink text-sm font-medium">
          {label}
        </label>
        <select
          id={id}
          name={name}
          value={selectValue}
          required={config.required || undefined}
          disabled={disabled || selectDisabled}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={errorId}
          className={`${selectClass} ${error ? "border-red-500" : "border-sand/70 hover:border-ink/25"} ${selectClassName}`}
          onChange={(event) => config.onChange(event.target.value)}
        >
          <option value="">{placeholder}</option>
          {options.map((optionName) => (
            <option key={optionName} value={optionName}>
              {optionName}
            </option>
          ))}
        </select>
        {error ? (
          <p id={errorId} role="alert" className="text-sm text-red-600">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`grid gap-4 sm:grid-cols-3 ${className}`}>
      {field({
        id: `${prefix}-department`,
        label: fieldLabels.department,
        name: fieldNames.department,
        selectValue: current.department,
        options: departmentOptions,
        placeholder: "Selecciona…",
        selectDisabled: false,
        required,
        error: errors?.department,
        onChange: (next) => emit({ department: next, province: "", district: "" }),
      })}
      {field({
        id: `${prefix}-province`,
        label: fieldLabels.province,
        name: fieldNames.province,
        selectValue: current.province,
        options: provinceOptions,
        placeholder: current.department ? "Selecciona…" : "Elige un departamento",
        selectDisabled: !current.department,
        required: false,
        error: errors?.province,
        onChange: (next) => emit({ ...current, province: next, district: "" }),
      })}
      {field({
        id: `${prefix}-district`,
        label: fieldLabels.district,
        name: fieldNames.district,
        selectValue: current.district,
        options: districtOptions,
        placeholder: current.province ? "Selecciona…" : "Elige una provincia",
        selectDisabled: !current.province,
        required: false,
        error: errors?.district,
        onChange: (next) => emit({ ...current, district: next }),
      })}
    </div>
  );
}
